<script setup lang="ts">
  import type { Agency, AgencyGroupNode } from '@gat-ship/types'
  import { isAgencyGroupNode, testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupCombobox,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    type SelectInputItem,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { IconMoveVertical } from '@gat-ship/icons'
  import { computed, inject, ref } from 'vue'
  import { agencyKey, agencyStructureKey } from '@gat-ship/types/injection-keys'

  const isOpen = defineModel<boolean>('open', { default: false })
  const agencyProvider = inject(agencyKey)
  const agencyStructureProvider = inject(agencyStructureKey)

  // Source candidate parent groups from the structure tree so each option keeps
  // its hierarchy `level` for indentation — the flat ref-data list no longer
  // carries it.
  const loadingGroups = ref(false)
  const preSelectedAgencyGroupId = ref<string | undefined>(undefined)
  const description = ref('')

  async function ensureGroupsLoaded() {
    if (!agencyStructureProvider) return
    loadingGroups.value = true
    try {
      await agencyStructureProvider.fetchAgencyStructure()
    } finally {
      loadingGroups.value = false
    }
  }

  const groupNodes = computed<AgencyGroupNode[]>(() =>
    (agencyStructureProvider?.agencyStructure.value ?? []).filter(
      isAgencyGroupNode
    )
  )

  const groupItems = computed<SelectInputItem<AgencyGroupNode>[]>(() => {
    const items: SelectInputItem<AgencyGroupNode>[] = groupNodes.value
      .filter((g) => !g.isInactive)
      .map((g) => ({
        value: g.id,
        title: g.name,
        searchValue: `${g.name} ${g.shortName}`,
        metaData: g
      }))
    // Keep the current parent group visible even if it's filtered out.
    const preId = preSelectedAgencyGroupId.value
    if (preId && !items.find((i) => i.value === preId)) {
      const node = groupNodes.value.find((g) => g.id === preId)
      if (node) {
        items.push({
          value: node.id,
          title: node.name,
          searchValue: `${node.name} ${node.shortName}`,
          metaData: node
        })
      }
    }
    return items
  })

  function getItemTitle(item: AgencyGroupNode) {
    const duplicates = groupNodes.value.filter((i) => i.name === item.name)
    if (duplicates.length > 1) {
      return { name: item.name, shortName: item.shortName }
    }
    return { name: item.name, shortName: undefined as string | undefined }
  }

  const moveAgencySchema = z.object({
    agencyId: z.string().min(1),
    parentGroupId: z.string().min(1, 'Please select a group')
  })
  type MoveAgencyFormValues = z.infer<typeof moveAgencySchema>

  const form = useForm({
    defaultValues: {
      agencyId: '',
      parentGroupId: ''
    } as MoveAgencyFormValues,
    validators: {
      onChange: moveAgencySchema
    },
    onSubmit: async ({ value }) => {
      await agencyProvider?.commands.move(value.agencyId, {
        parentGroupId: value.parentGroupId
      })
      isOpen.value = false
    }
  })

  export interface ShowMoveAgencyModalParams {
    agency: Agency
  }

  function showModal({ agency }: ShowMoveAgencyModalParams) {
    description.value = agency.name
    preSelectedAgencyGroupId.value = agency.parentGroup?.id
    form.reset({
      agencyId: agency.id,
      parentGroupId: agency.parentGroup?.id ?? ''
    })
    isOpen.value = true
    ensureGroupsLoaded()
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconMoveVertical class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Move Agency
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FormField
          :form="form"
          name="parentGroupId"
          label="New Parent Group"
          required
        >
          <template #default="{ field, invalid }">
            <InputGroupCombobox
              :id="field.name"
              :model-value="field.state.value"
              :items="groupItems"
              :loading="loadingGroups"
              placeholder="Select Group"
              :aria-invalid="invalid"
              :data-testid="testIds.common.select.agencyGroup"
              @update:model-value="field.handleChange"
              @blur="field.handleBlur"
            >
              <template #item="{ item }">
                <div
                  class="flex grow items-center"
                  :style="{
                    paddingLeft: item.metaData
                      ? `${item.metaData.level * 8}px`
                      : '0'
                  }"
                >
                  <div>{{ getItemTitle(item.metaData!).name }}</div>
                  <div
                    v-if="getItemTitle(item.metaData!).shortName"
                    class="text-muted-foreground pl-2 text-xs"
                  >
                    ({{ getItemTitle(item.metaData!).shortName }})
                  </div>
                </div>
              </template>
            </InputGroupCombobox>
          </template>
        </FormField>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Move" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
